import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Wallet, Calendar, LogOut, DollarSign, CheckCircle, Clock,
  RefreshCw, Shield, AlertTriangle, Search, Phone, FileText, TrendingUp, 
  XCircle, Download, Eye, Ban, Trash2, CreditCard, UserCheck, 
  ArrowLeft, Star, MapPin, Mail, X, LayoutDashboard, Receipt, 
  ArrowUpDown, Banknote, BarChart3, Menu, ChevronRight, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { 
  getAdminStats, getAllUsers, getAllBookings, getAllTransactions,
  payProvider, getSOSAlerts, getAllWithdrawals, processWithdrawal,
  searchUserByMobile, approveUser, deactivateUser, bulkApproveUsers, getMonthlyReport,
  getUserDetails, grantMembership, bulkGrantMembership, blockUser, unblockUser,
  deleteUser, markUserPaid, updateBookingSettlement, downloadReport,
  getPendingPayments, getPaymentScreenshot, approvePayment, rejectPayment,
  getAdminInvoicePdfUrl, getAdminUserInvoices
} from '../services/api';
import { getToken } from '../services/tokenStore';
import AdminInvoicesModal from '../components/AdminInvoicesModal';
import { toast } from 'sonner';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'bookings', label: 'Bookings', icon: Calendar },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'settlements', label: 'Settlements', icon: ArrowUpDown },
  { id: 'withdrawals', label: 'Withdrawals', icon: Banknote },
  { id: 'transactions', label: 'Transactions', icon: Receipt },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

const PAGE_SIZE = 100;

// Pagination Component
const Pagination = ({ page, pages, total, onPageChange, label }) => {
  if (pages <= 1) return null;
  const maxButtons = 5;
  let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
  let endPage = Math.min(pages, startPage + maxButtons - 1);
  if (endPage - startPage < maxButtons - 1) startPage = Math.max(1, endPage - maxButtons + 1);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
      <p className="text-xs text-gray-500">
        Showing <strong>{((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, total)}</strong> of <strong>{total.toLocaleString()}</strong> {label}
      </p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="prev-page">
          <ChevronLeft className="w-4 h-4" />
        </button>
        {startPage > 1 && <><button onClick={() => onPageChange(1)} className="w-8 h-8 text-xs rounded-lg hover:bg-gray-200">1</button>{startPage > 2 && <span className="text-gray-400 text-xs px-1">...</span>}</>}
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(p => (
          <button key={p} onClick={() => onPageChange(p)} className={`w-8 h-8 text-xs rounded-lg font-medium ${p === page ? 'bg-gray-900 text-white' : 'hover:bg-gray-200 text-gray-600'}`}>{p}</button>
        ))}
        {endPage < pages && <>{endPage < pages - 1 && <span className="text-gray-400 text-xs px-1">...</span>}<button onClick={() => onPageChange(pages)} className="w-8 h-8 text-xs rounded-lg hover:bg-gray-200">{pages}</button></>}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages} className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="next-page">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { isAdmin, logout, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [bookings, setBookings] = useState([]);
  const [bookingsPagination, setBookingsPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [sosAlerts, setSOSAlerts] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [bulkPlan, setBulkPlan] = useState('6_months');
  const [reportDates, setReportDates] = useState({ start: '', end: '' });
  const [pendingPayments, setPendingPayments] = useState([]);
  const [viewScreenshot, setViewScreenshot] = useState({ open: false, data: null, paymentId: null });
  const [paymentActionLoading, setPaymentActionLoading] = useState(null);
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [invoicesModal, setInvoicesModal] = useState({ open: false, user: null, invoices: [], loading: false });
  const [invoicePreview, setInvoicePreview] = useState({ url: null, invoiceNo: null });

  useEffect(() => {
    if (!token || !isAdmin) { navigate('/auth?admin=true'); return; }
    fetchOverviewData();
    // fetchOverviewData is a stable useCallback (empty deps) so we intentionally
    // re-run only when auth identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin, navigate]);

  // Fetch only overview data on load (fast)
  const fetchOverviewData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, recentUsersRes, recentBookingsRes, sosRes, monthlyRes, pendingRes, withdrawRes] = await Promise.all([
        getAdminStats().catch(() => ({ data: {} })),
        getAllUsers(1, 10).catch(() => ({ data: { users: [] } })),
        getAllBookings(1, 10).catch(() => ({ data: { bookings: [] } })),
        getSOSAlerts().catch(() => ({ data: { alerts: [] } })),
        getMonthlyReport().catch(() => ({ data: null })),
        getPendingPayments().catch(() => ({ data: { payments: [] } })),
        getAllWithdrawals().catch(() => ({ data: { withdrawals: [] } }))
      ]);
      if (statsRes.data) setStats(statsRes.data);
      setRecentUsers(recentUsersRes.data.users || []);
      setRecentBookings(recentBookingsRes.data.bookings || []);
      setSOSAlerts(sosRes.data.alerts || []);
      if (monthlyRes.data) setMonthlyReport(monthlyRes.data);
      setPendingPayments(pendingRes.data.payments || []);
      setWithdrawals(withdrawRes.data.withdrawals || []);
    } catch (error) {
      console.error('fetchOverviewData failed', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch paginated users
  const fetchUsers = useCallback(async (page = 1, filter = 'all', search = '') => {
    try {
      const res = await getAllUsers(page, PAGE_SIZE, filter, search);
      setUsers(res.data.users || []);
      setUsersPagination({ total: res.data.total || 0, page: res.data.page || 1, pages: res.data.pages || 1 });
    } catch (e) { console.error('fetchUsers failed', e); toast.error('Failed to fetch users'); }
  }, []);

  // Fetch paginated bookings
  const fetchBookings = useCallback(async (page = 1) => {
    try {
      const res = await getAllBookings(page, PAGE_SIZE);
      setBookings(res.data.bookings || []);
      setBookingsPagination({ total: res.data.total || 0, page: res.data.page || 1, pages: res.data.pages || 1 });
    } catch (e) { console.error('fetchBookings failed', e); toast.error('Failed to fetch bookings'); }
  }, []);

  // Fetch paginated transactions
  const fetchTransactions = useCallback(async (page = 1) => {
    try {
      const res = await getAllTransactions(page, PAGE_SIZE);
      setTransactions(res.data.transactions || []);
      setTransactionsPagination({ total: res.data.total || 0, page: res.data.page || 1, pages: res.data.pages || 1 });
    } catch (e) { console.error('fetchTransactions failed', e); toast.error('Failed to fetch transactions'); }
  }, []);

  // Load tab data on tab change. fetchUsers/Bookings/Transactions are stable
  // useCallbacks so it's safe to omit them from deps without staleness.
  useEffect(() => {
    if (!token || !isAdmin) return;
    if (activeTab === 'users') fetchUsers(1, userFilter, userSearch);
    else if (activeTab === 'bookings') fetchBookings(1);
    else if (activeTab === 'transactions') fetchTransactions(1);
    else if (activeTab === 'settlements') fetchBookings(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token, isAdmin]);

  // Refetch users when filter changes
  useEffect(() => {
    if (activeTab === 'users') fetchUsers(1, userFilter, userSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter]);

  const handleUserSearch = () => {
    fetchUsers(1, userFilter, userSearch);
  };

  const handleDownloadInvoice = async (orderId, invoiceNo) => {
    try {
      const { url, token: tk } = getAdminInvoicePdfUrl(orderId);
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${tk}` } });
      if (!resp.ok) {
        const txt = await resp.text();
        toast.error(`Download failed: ${resp.status} ${txt.slice(0, 80)}`);
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${invoiceNo || orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success('Invoice downloaded');
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const handlePreviewInvoice = async (orderId, invoiceNo) => {
    try {
      const { url, token: tk } = getAdminInvoicePdfUrl(orderId);
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${tk}` } });
      if (!resp.ok) {
        toast.error(`Preview failed: ${resp.status}`);
        return;
      }
      // Re-wrap as inline PDF blob so the iframe renders it instead of
      // triggering a download (we drop the attachment header).
      const blob = new Blob([await resp.blob()], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      // Revoke any previously-active preview before swapping. URL.revokeObjectURL
      // throws if the URL was already invalidated — the empty catch is intentional.
      setInvoicePreview((prev) => {
        if (prev.url) {
          try { URL.revokeObjectURL(prev.url); } catch (_) { /* already revoked */ }
        }
        return { url: blobUrl, invoiceNo };
      });
    } catch {
      toast.error('Preview failed');
    }
  };

  const closeInvoicesModal = () => {
    if (invoicePreview.url) {
      // URL.revokeObjectURL throws on already-invalid URLs; safe to swallow.
      try { URL.revokeObjectURL(invoicePreview.url); } catch (_) { /* already revoked */ }
    }
    setInvoicePreview({ url: null, invoiceNo: null });
    setInvoicesModal({ open: false, user: null, invoices: [], loading: false });
  };

  const handleOpenInvoices = async (u) => {
    setInvoicesModal({ open: true, user: u, invoices: [], loading: true });
    try {
      const res = await getAdminUserInvoices(u.id);
      const list = res.data.invoices || [];
      setInvoicesModal({ open: true, user: u, invoices: list, loading: false });
      if (list.length === 0) toast.info('This user has no paid invoices yet.');
    } catch {
      toast.error('Failed to load invoices');
      setInvoicesModal({ open: true, user: u, invoices: [], loading: false });
    }
  };

  const handleAction = async (action, ...args) => {
    try {
      const res = await action(...args);
      toast.success(res.data.message || 'Action completed');
      // Refresh current view
      if (activeTab === 'users') fetchUsers(usersPagination.page, userFilter, userSearch);
      else if (activeTab === 'bookings' || activeTab === 'settlements') fetchBookings(bookingsPagination.page);
      fetchOverviewData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleGlobalSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await searchUserByMobile(searchQuery);
      setSearchResults(res.data);
    } catch { toast.error('Search failed'); }
  };

  const handleViewUser = async (userId) => {
    try {
      const res = await getUserDetails(userId);
      setUserDetail(res.data);
      setShowUserDetail(true);
    } catch { toast.error('Failed to load user details'); }
  };

  const handleGrantMembership = async (userId, planId) => {
    await handleAction(grantMembership, userId, planId);
  };

  const handleBulkGrantMembership = async () => {
    if (selectedUsers.size === 0) return;
    try {
      const res = await bulkGrantMembership([...selectedUsers], bulkPlan);
      toast.success(res.data.message);
      setSelectedUsers(new Set());
      fetchUsers(usersPagination.page, userFilter, userSearch);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`DELETE user "${name}" and ALL their data? This cannot be undone.`)) return;
    await handleAction(deleteUser, userId);
    setShowUserDetail(false);
  };

  const handleDownloadReport = async (type) => {
    try {
      const res = await downloadReport(type, reportDates.start || null, reportDates.end || null);
      const data = res.data.data;
      if (!data || data.length === 0) { toast.error('No data to download'); return; }
      const headers = Object.keys(data[0]);
      const csv = [headers.join(','), ...data.map(row => headers.map(h => {
        const val = row[h];
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : (val ?? '');
      }).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${type}_report_${new Date().toISOString().split('T')[0]}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${type} report downloaded`);
    } catch { toast.error('Download failed'); }
  };

  const handleSettlement = async (bookingId, status) => {
    await handleAction(updateBookingSettlement, bookingId, status);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const badge = (text, color) => (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${color}`}>{text}</span>
  );

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-100 text-amber-700', accepted: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return badge(status, map[status] || 'bg-gray-100 text-gray-600');
  };

  if (!isAdmin) return null;

  // ===================== USER DETAIL VIEW =====================
  if (showUserDetail && userDetail) {
    const u = userDetail.user;
    return (
      <div className="min-h-screen bg-[#f8f9fb]" data-testid="admin-user-detail">
        <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-6 h-14 flex items-center gap-4">
          <button onClick={() => setShowUserDetail(false)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className="text-gray-300">|</span>
          <span className="font-semibold text-gray-900">{u.name}</span>
        </div>
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900" data-testid="detail-user-name">{u.name}</h2>
                <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{u.mobile}</span>
                  {u.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{u.email}</span>}
                  {u.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{u.city} {u.pincode}</span>}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {u.provider_active ? badge('Provider', 'bg-orange-100 text-orange-700') : badge('Hirer', 'bg-teal-100 text-teal-700')}
                  {u.membership && badge(u.membership.replace('_', ' '), 'bg-purple-100 text-purple-700')}
                  {u.admin_approved ? badge('Approved', 'bg-green-100 text-green-700') : badge('Unapproved', 'bg-yellow-100 text-yellow-700')}
                  {u.blocked && badge('BLOCKED', 'bg-red-100 text-red-700')}
                  {u.membership_active && badge('Membership Active', 'bg-indigo-100 text-indigo-700')}
                  {u.id_proof_path
                    ? badge('ID Proof On File', 'bg-emerald-100 text-emerald-700')
                    : (u.id_proof_required ? badge('ID Proof Pending', 'bg-red-100 text-red-700') : null)}
                </div>
                {u.id_proof_path && (
                  <div className="mt-3">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${u.id}/id-proof`}
                      target="_blank" rel="noreferrer"
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const tk = getToken();
                          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${u.id}/id-proof`, { headers: { Authorization: `Bearer ${tk}` } });
                          if (!r.ok) { toast.error('Could not fetch ID proof'); return; }
                          const blob = await r.blob();
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank', 'noopener');
                          setTimeout(() => URL.revokeObjectURL(url), 30000);
                        } catch { toast.error('Could not fetch ID proof'); }
                      }}
                      className="inline-flex items-center gap-2 text-xs font-medium text-blue-600 hover:underline"
                      data-testid="admin-view-id-proof"
                    >
                      <Eye className="w-3.5 h-3.5" /> View ID Proof
                    </a>
                  </div>
                )}
              </div>
              <div className="text-right space-y-1 text-sm">
                <p className="text-gray-500">Joined: {new Date(u.created_at).toLocaleDateString()}</p>
                <p className="font-semibold text-green-600">Earnings: Rs.{u.total_earnings || 0}</p>
                <p className="text-orange-600">Pending: Rs.{u.pending_earnings || 0}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-100">
              {!u.admin_approved && <Button size="sm" onClick={() => handleAction(approveUser, u.id)} className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs" data-testid="detail-approve-btn"><UserCheck className="w-3 h-3 mr-1" /> Approve</Button>}
              {u.admin_approved && !u.blocked && <Button size="sm" variant="outline" onClick={() => handleAction(deactivateUser, u.id)} className="border-yellow-300 text-yellow-700 rounded-lg text-xs"><XCircle className="w-3 h-3 mr-1" /> Deactivate</Button>}
              {!u.blocked ? <Button size="sm" variant="outline" onClick={() => handleAction(blockUser, u.id)} className="border-red-300 text-red-600 rounded-lg text-xs"><Ban className="w-3 h-3 mr-1" /> Block</Button> : <Button size="sm" onClick={() => handleAction(unblockUser, u.id)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs"><CheckCircle className="w-3 h-3 mr-1" /> Unblock</Button>}
              {(u.pending_earnings || 0) > 0 && <Button size="sm" onClick={() => handleAction(markUserPaid, u.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs"><CreditCard className="w-3 h-3 mr-1" /> Mark All Paid (Rs.{u.pending_earnings})</Button>}
              <select onChange={(e) => e.target.value && handleGrantMembership(u.id, e.target.value)} className="text-xs border rounded-lg px-2 py-1.5 bg-white" defaultValue=""><option value="" disabled>Grant Membership...</option><option value="6_months">6 Months</option><option value="5_years">5 Years</option><option value="lifetime">Lifetime</option></select>
              <Button size="sm" variant="outline" onClick={() => handleDeleteUser(u.id, u.name)} className="border-red-400 text-red-700 rounded-lg text-xs ml-auto"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {[
              { title: 'Bookings as Hirer', data: userDetail.hirer_bookings, color: 'border-t-teal-500', render: b => <><div className="flex justify-between"><span className="font-medium">{b.service_name}</span>{statusBadge(b.status)}</div><p className="text-gray-500 text-xs mt-1">Provider: {b.provider_name} | {b.hours}h | Rs.{b.total_amount}</p></> },
              { title: 'Bookings as Provider', data: userDetail.provider_bookings, color: 'border-t-orange-500', render: b => <><div className="flex justify-between"><span className="font-medium">{b.service_name}</span>{statusBadge(b.status)}</div><p className="text-gray-500 text-xs mt-1">Hirer: {b.hirer_name} | {b.hours}h | Earnings: Rs.{b.provider_earnings} | {b.provider_paid ? 'PAID' : 'UNPAID'}</p></> },
              { title: 'Transactions', data: userDetail.transactions, color: 'border-t-purple-500', render: t => <div className="flex justify-between"><div><span className="font-medium">{t.type}</span><p className="text-xs text-gray-500">{t.invoice_number}</p></div><span className="font-semibold">Rs.{t.amount}</span></div> },
              { title: 'Reviews', data: userDetail.reviews_received, color: 'border-t-amber-500', render: (r) => <div><div className="flex gap-0.5">{[...Array(r.rating || 0)].map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400" />)}</div><p className="text-gray-600 text-xs mt-1">{r.review}</p></div> }
            ].map((section) => (
              <div key={section.title} className={`bg-white rounded-xl border border-gray-200 border-t-4 ${section.color} overflow-hidden`}>
                <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-sm text-gray-900">{section.title} ({section.data.length})</h3></div>
                <div className="max-h-64 overflow-y-auto">
                  {section.data.length === 0 ? <p className="p-4 text-gray-400 text-sm">None</p> :
                    section.data.map((item, i) => <div key={item.id || i} className="p-3 border-b border-gray-50 text-sm">{section.render(item, i)}</div>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ===================== MAIN LAYOUT =====================
  return (
    <div className="min-h-screen bg-[#f8f9fb] flex" data-testid="admin-dashboard">
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-white border-r border-gray-200 z-50 flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-14 px-5 flex items-center gap-3 border-b border-gray-100">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-rose-500 rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="font-bold text-sm text-gray-900 leading-none">HumanOnRent.AI</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Admin Panel</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
              data-testid={`nav-${tab.id}`}>
              <tab.icon className="w-4 h-4 flex-shrink-0" />{tab.label}
              {tab.id === 'payments' && pendingPayments.length > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{pendingPayments.length}</span>}
              {tab.id === 'withdrawals' && withdrawals.filter(w => w.status === 'pending').length > 0 && <span className="ml-auto bg-amber-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{withdrawals.filter(w => w.status === 'pending').length}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-100">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={fetchOverviewData} className="flex-1 text-xs rounded-lg" data-testid="admin-refresh-btn"><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
            <Button size="sm" variant="outline" onClick={logout} className="text-xs rounded-lg text-red-600 border-red-200 hover:bg-red-50" data-testid="admin-logout-btn"><LogOut className="w-3 h-3" /></Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-gray-200 px-6 h-14 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg"><Menu className="w-5 h-5 text-gray-600" /></button>
          <h1 className="font-semibold text-gray-900 text-lg">{TABS.find(t => t.id === activeTab)?.label}</h1>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <Input placeholder="Search mobile, name, email, city..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-64 h-9 text-sm" data-testid="admin-mobile-search" onKeyDown={e => e.key === 'Enter' && handleGlobalSearch()} />
              <Button size="sm" onClick={handleGlobalSearch} className="bg-gray-900 hover:bg-gray-800 text-white h-9 rounded-lg text-xs" data-testid="admin-search-btn"><Search className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        </div>

        <div className="p-6 max-w-[1400px]">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
          ) : (
            <>
              {/* Global Search Results */}
              {searchResults !== null && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm text-gray-900">Search Results — {searchResults.total || searchResults.users?.length || 0} found</h3>
                    <button onClick={() => { setSearchResults(null); setSearchQuery(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                  </div>
                  {(!searchResults.users || searchResults.users.length === 0) ? <p className="text-gray-400 text-sm">No results</p> :
                    searchResults.users.map(u => (
                      <div key={u.id} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{u.name} <span className="text-gray-400 text-xs ml-1">{u.mobile}</span></p>
                          <div className="flex gap-1 mt-1">
                            {u.provider_active ? badge('Provider', 'bg-orange-100 text-orange-700') : badge('Hirer', 'bg-teal-100 text-teal-700')}
                            {u.city && <span className="text-xs text-gray-400">{u.city}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => handleViewUser(u.id)} className="rounded-lg text-xs h-8"><Eye className="w-3 h-3 mr-1" /> View</Button>
                          {!u.admin_approved && <Button size="sm" onClick={() => handleAction(approveUser, u.id)} className="bg-green-600 text-white rounded-lg text-xs h-8"><CheckCircle className="w-3 h-3" /></Button>}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* SOS Alerts */}
              {sosAlerts.length > 0 && activeTab === 'overview' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-red-900 flex items-center gap-2 text-sm mb-3"><AlertTriangle className="w-4 h-4" /> SOS Alerts ({sosAlerts.length})</h3>
                  {sosAlerts.slice(0, 3).map(a => (
                    <div key={a.id} className="bg-white rounded-lg p-3 mb-2 border border-red-100 text-sm">
                      <span className="font-medium">{a.user_name}</span> ({a.user_mobile}) — {a.message}
                    </div>
                  ))}
                </div>
              )}

              {/* ===== OVERVIEW ===== */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: "Total Users", value: stats?.total_users || 0, icon: Users, accent: 'text-blue-600 bg-blue-50' },
                      { label: "Providers", value: stats?.total_providers || 0, icon: UserCheck, accent: 'text-orange-600 bg-orange-50' },
                      { label: "Hirers", value: stats?.total_hirers || 0, icon: Users, accent: 'text-teal-600 bg-teal-50' },
                      { label: "Total Bookings", value: stats?.total_bookings || 0, icon: Calendar, accent: 'text-purple-600 bg-purple-50' },
                      { label: "Pending Bookings", value: stats?.pending_bookings || 0, icon: Clock, accent: 'text-amber-600 bg-amber-50' },
                      { label: "Completed", value: stats?.completed_bookings || 0, icon: CheckCircle, accent: 'text-green-600 bg-green-50' },
                      { label: "Pending Approval", value: stats?.pending_approval || 0, icon: Shield, accent: 'text-red-600 bg-red-50' },
                      { label: "Members Active", value: stats?.membership_active || 0, icon: Star, accent: 'text-indigo-600 bg-indigo-50' }
                    ].map((stat, idx) => (
                      <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.accent}`}><stat.icon className="w-5 h-5" /></div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
                            <p className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: "Total Revenue", value: `Rs.${(stats?.total_revenue || 0).toLocaleString()}`, bg: "bg-gray-900" },
                      { label: "Commission (20%)", value: `Rs.${(stats?.total_commission || 0).toLocaleString()}`, bg: "bg-emerald-600" },
                      { label: "Membership Rev.", value: `Rs.${(stats?.membership_revenue || 0).toLocaleString()}`, bg: "bg-indigo-600" },
                      { label: "This Month", value: `Rs.${(monthlyReport?.stats?.total_revenue || 0).toLocaleString()}`, bg: "bg-purple-600" }
                    ].map((stat, idx) => (
                      <div key={idx} className={`${stat.bg} text-white rounded-xl p-5`}>
                        <p className="text-xs text-white/70 font-medium">{stat.label}</p>
                        <p className="text-2xl font-bold mt-1">{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl border border-gray-200">
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-semibold text-sm text-gray-900">Recent Users</h3>
                        <button onClick={() => setActiveTab('users')} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">View All <ChevronRight className="w-3 h-3" /></button>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {recentUsers.slice(0, 5).map(u => (
                          <div key={u.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer" onClick={() => handleViewUser(u.id)}>
                            <div>
                              <p className="font-medium text-sm text-gray-900">{u.name}</p>
                              <p className="text-xs text-gray-500">{u.mobile} {u.city && `- ${u.city}`}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {u.provider_active ? badge('Provider', 'bg-orange-100 text-orange-700') : badge('Hirer', 'bg-teal-100 text-teal-700')}
                              {u.admin_approved ? badge('Active', 'bg-green-100 text-green-700') : badge('Pending', 'bg-amber-100 text-amber-700')}
                            </div>
                          </div>
                        ))}
                        {recentUsers.length === 0 && <p className="p-5 text-gray-400 text-sm text-center">No users yet</p>}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200">
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-semibold text-sm text-gray-900">Recent Bookings</h3>
                        <button onClick={() => setActiveTab('bookings')} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">View All <ChevronRight className="w-3 h-3" /></button>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {recentBookings.slice(0, 5).map(b => (
                          <div key={b.id} className="px-5 py-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm text-gray-900">{b.service_name}</p>
                              <p className="text-xs text-gray-500">{b.hirer_name} hired {b.provider_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-gray-900">Rs.{b.total_amount}</span>
                              {statusBadge(b.status)}
                            </div>
                          </div>
                        ))}
                        {recentBookings.length === 0 && <p className="p-5 text-gray-400 text-sm text-center">No bookings yet</p>}
                      </div>
                    </div>
                  </div>
                  {monthlyReport && (
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="font-semibold text-sm text-gray-900 mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-purple-600" /> Monthly Summary — {monthlyReport.month}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                          { label: "Bookings", value: monthlyReport.stats?.total_bookings || 0 },
                          { label: "Booking Rev.", value: `Rs.${monthlyReport.stats?.booking_revenue || 0}` },
                          { label: "Commission", value: `Rs.${monthlyReport.stats?.commission_earned || 0}` },
                          { label: "Membership Rev.", value: `Rs.${monthlyReport.stats?.membership_revenue || 0}` },
                          { label: "New Users", value: monthlyReport.stats?.new_users || 0 }
                        ].map((s, i) => (
                          <div key={i} className="bg-gray-50 rounded-lg p-4 text-center">
                            <p className="text-xs text-gray-500">{s.label}</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{s.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== USERS ===== */}
              {activeTab === 'users' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-sm">All Users ({usersPagination.total.toLocaleString()})</h3>
                      <select value={userFilter} onChange={e => setUserFilter(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="user-filter">
                        <option value="all">All</option>
                        <option value="providers">Providers</option>
                        <option value="hirers">Hirers</option>
                        <option value="blocked">Blocked</option>
                        <option value="unpaid">No Membership</option>
                        <option value="pending">Pending Approval</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <Input placeholder="Search in table..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="w-40 text-xs h-8" onKeyDown={e => e.key === 'Enter' && handleUserSearch()} />
                        <Button size="sm" onClick={handleUserSearch} className="bg-gray-800 text-white h-8 text-xs rounded-lg"><Search className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    {selectedUsers.size > 0 && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => handleAction(bulkApproveUsers, [...selectedUsers])} className="bg-green-600 text-white rounded-lg text-xs h-8"><CheckCircle className="w-3 h-3 mr-1" /> Approve ({selectedUsers.size})</Button>
                        <select value={bulkPlan} onChange={e => setBulkPlan(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5"><option value="6_months">6 Months</option><option value="5_years">5 Years</option><option value="lifetime">Lifetime</option></select>
                        <Button size="sm" onClick={handleBulkGrantMembership} className="bg-purple-600 text-white rounded-lg text-xs h-8"><CreditCard className="w-3 h-3 mr-1" /> Grant ({selectedUsers.size})</Button>
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                        <tr>
                          <th className="p-3 w-8"><input type="checkbox" onChange={e => e.target.checked ? setSelectedUsers(new Set(users.map(u => u.id))) : setSelectedUsers(new Set())} /></th>
                          <th className="p-3 text-left font-medium">User</th>
                          <th className="p-3 text-left font-medium">Location</th>
                          <th className="p-3 text-left font-medium">Type</th>
                          <th className="p-3 text-left font-medium">Membership</th>
                          <th className="p-3 text-left font-medium">Status</th>
                          <th className="p-3 text-left font-medium">Earnings</th>
                          <th className="p-3 text-left font-medium">Joined</th>
                          <th className="p-3 text-left font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.id} className={`border-t border-gray-50 hover:bg-gray-50/70 ${u.blocked ? 'bg-red-50/30' : ''}`}>
                            <td className="p-3"><input type="checkbox" checked={selectedUsers.has(u.id)} onChange={() => toggleUserSelection(u.id)} /></td>
                            <td className="p-3"><p className="font-medium text-gray-900">{u.name}</p><p className="text-xs text-gray-400">{u.mobile}</p>{u.email && <p className="text-xs text-gray-400">{u.email}</p>}</td>
                            <td className="p-3 text-gray-500 text-xs">{u.city ? `${u.city} ${u.pincode || ''}` : '-'}</td>
                            <td className="p-3">{u.provider_active ? badge('Provider', 'bg-orange-100 text-orange-700') : badge('Hirer', 'bg-teal-100 text-teal-700')}</td>
                            <td className="p-3">{u.membership ? badge(u.membership.replace('_', ' '), 'bg-purple-100 text-purple-700') : <span className="text-gray-400 text-xs">None</span>}</td>
                            <td className="p-3">{u.blocked ? badge('Blocked', 'bg-red-100 text-red-700') : u.admin_approved ? badge('Active', 'bg-green-100 text-green-700') : badge('Pending', 'bg-amber-100 text-amber-700')}</td>
                            <td className="p-3 font-semibold text-green-600">Rs.{u.total_earnings || 0}</td>
                            <td className="p-3 text-gray-400 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                            <td className="p-3">
                              <div className="flex gap-1">
                                <button onClick={() => handleViewUser(u.id)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="View"><Eye className="w-3.5 h-3.5 text-gray-600" /></button>
                                <button onClick={() => handleOpenInvoices(u)} className="p-1.5 hover:bg-purple-50 rounded-lg" title="Tax Invoices" data-testid={`user-invoices-${u.id}`}><Receipt className="w-3.5 h-3.5 text-purple-600" /></button>
                                {!u.admin_approved && <button onClick={() => handleAction(approveUser, u.id)} className="p-1.5 hover:bg-green-50 rounded-lg" title="Approve"><UserCheck className="w-3.5 h-3.5 text-green-600" /></button>}
                                {!u.blocked ? <button onClick={() => handleAction(blockUser, u.id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Block"><Ban className="w-3.5 h-3.5 text-red-500" /></button> : <button onClick={() => handleAction(unblockUser, u.id)} className="p-1.5 hover:bg-blue-50 rounded-lg" title="Unblock"><CheckCircle className="w-3.5 h-3.5 text-blue-600" /></button>}
                                <button onClick={() => handleDeleteUser(u.id, u.name)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {users.length === 0 && <div className="text-center py-12 text-gray-400 text-sm">No users match your filter</div>}
                  </div>
                  <Pagination page={usersPagination.page} pages={usersPagination.pages} total={usersPagination.total} label="users" onPageChange={(p) => fetchUsers(p, userFilter, userSearch)} />
                </div>
              )}

              {/* ===== BOOKINGS ===== */}
              {activeTab === 'bookings' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-sm">All Bookings ({bookingsPagination.total.toLocaleString()})</h3></div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                        <tr><th className="p-3 text-left font-medium">Service</th><th className="p-3 text-left font-medium">Hirer</th><th className="p-3 text-left font-medium">Provider</th><th className="p-3 font-medium">Date</th><th className="p-3 font-medium">Hours</th><th className="p-3 font-medium">Amount</th><th className="p-3 font-medium">GST</th><th className="p-3 font-medium">Commission</th><th className="p-3 font-medium">Status</th></tr>
                      </thead>
                      <tbody>
                        {bookings.map(b => (
                          <tr key={b.id} className="border-t border-gray-50 hover:bg-gray-50/70">
                            <td className="p-3 font-medium">{b.service_name}</td>
                            <td className="p-3"><div className="font-medium text-sm">{b.hirer_name}</div><div className="text-xs text-gray-400">{b.hirer_mobile}</div></td>
                            <td className="p-3"><div className="font-medium text-sm">{b.provider_name}</div><div className="text-xs text-gray-400">{b.provider_mobile}</div></td>
                            <td className="p-3 text-center text-xs">{b.date || new Date(b.created_at).toLocaleDateString()}</td>
                            <td className="p-3 text-center">{b.hours}h</td>
                            <td className="p-3 text-center font-semibold">Rs.{b.total_amount}</td>
                            <td className="p-3 text-center text-gray-500">Rs.{b.gst_amount || 0}</td>
                            <td className="p-3 text-center text-emerald-600 font-semibold">Rs.{b.commission}</td>
                            <td className="p-3 text-center">{statusBadge(b.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bookings.length === 0 && <div className="text-center py-12 text-gray-400 text-sm">No bookings yet</div>}
                  </div>
                  <Pagination page={bookingsPagination.page} pages={bookingsPagination.pages} total={bookingsPagination.total} label="bookings" onPageChange={fetchBookings} />
                </div>
              )}

              {/* ===== PAYMENTS ===== */}
              {activeTab === 'payments' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-sm">Pending Payment Verifications ({pendingPayments.length})</h3></div>
                  {pendingPayments.length === 0 ? <div className="p-12 text-center text-gray-400 text-sm">No pending payments</div> :
                    <div className="divide-y divide-gray-50">
                      {pendingPayments.map(payment => (
                        <div key={payment.order_id} className="p-4 hover:bg-gray-50/70">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {badge(payment.type === 'membership' ? 'Membership' : 'Booking', payment.type === 'membership' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')}
                                <span className="text-xs text-gray-400">{payment.order_id}</span>
                              </div>
                              <p className="font-medium text-gray-900 text-sm">{payment.user_name || 'N/A'} ({payment.user_mobile})</p>
                              <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-600">
                                <span>Amount: <strong className="text-amber-600">Rs.{payment.amount}</strong></span>
                                <span>UTR: <strong>{payment.utr_number}</strong></span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={async () => { try { const res = await getPaymentScreenshot(payment.order_id); setViewScreenshot({ open: true, data: res.data.screenshot, paymentId: payment.order_id }); } catch { toast.error('Failed'); } }} className="text-xs h-8 rounded-lg"><Eye className="w-3 h-3 mr-1" /> View</Button>
                              <Button size="sm" onClick={async () => { setPaymentActionLoading(payment.order_id); try { await approvePayment(payment.order_id); toast.success('Approved!'); setPendingPayments(prev => prev.filter(p => p.order_id !== payment.order_id)); fetchOverviewData(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setPaymentActionLoading(null); } }} disabled={paymentActionLoading === payment.order_id} className="bg-green-600 text-white text-xs h-8 rounded-lg"><CheckCircle className="w-3 h-3 mr-1" /> Approve</Button>
                              <Button size="sm" variant="destructive" onClick={async () => { setPaymentActionLoading(payment.order_id); try { await rejectPayment(payment.order_id); toast.success('Rejected.'); setPendingPayments(prev => prev.filter(p => p.order_id !== payment.order_id)); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setPaymentActionLoading(null); } }} disabled={paymentActionLoading === payment.order_id} className="text-xs h-8 rounded-lg"><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>}
                </div>
              )}

              {/* ===== SETTLEMENTS ===== */}
              {activeTab === 'settlements' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-sm">Payment Settlements</h3></div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide"><tr><th className="p-3 text-left font-medium">Booking</th><th className="p-3 text-left font-medium">Provider</th><th className="p-3 font-medium">Earnings</th><th className="p-3 font-medium">Total</th><th className="p-3 font-medium">Status</th><th className="p-3 font-medium">Action</th></tr></thead>
                      <tbody>
                        {bookings.filter(b => b.status === 'completed').map(b => (
                          <tr key={b.id} className="border-t border-gray-50 hover:bg-gray-50/70">
                            <td className="p-3"><div className="font-medium">{b.service_name}</div><div className="text-xs text-gray-400">Hirer: {b.hirer_name}</div></td>
                            <td className="p-3"><div className="font-medium">{b.provider_name}</div></td>
                            <td className="p-3 text-center font-semibold text-green-600">Rs.{b.provider_earnings}</td>
                            <td className="p-3 text-center font-semibold">Rs.{b.total_amount}</td>
                            <td className="p-3 text-center">{b.provider_paid ? badge('PAID', 'bg-green-100 text-green-700') : badge('UNPAID', 'bg-red-100 text-red-700')}</td>
                            <td className="p-3 text-center">{!b.provider_paid ? <Button size="sm" onClick={() => handleSettlement(b.id, 'paid')} className="bg-emerald-600 text-white rounded-lg text-xs h-8">Mark Paid</Button> : <Button size="sm" variant="outline" onClick={() => handleSettlement(b.id, 'unpaid')} className="border-red-300 text-red-600 rounded-lg text-xs h-8">Mark Unpaid</Button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bookings.filter(b => b.status === 'completed').length === 0 && <div className="text-center py-12 text-gray-400 text-sm">No completed bookings</div>}
                  </div>
                </div>
              )}

              {/* ===== WITHDRAWALS ===== */}
              {activeTab === 'withdrawals' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-sm">Withdrawal Requests ({withdrawals.length})</h3></div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide"><tr><th className="p-3 text-left font-medium">User</th><th className="p-3 font-medium">Amount</th><th className="p-3 font-medium">UPI</th><th className="p-3 font-medium">Status</th><th className="p-3 font-medium">Date</th><th className="p-3 font-medium">Action</th></tr></thead>
                      <tbody>
                        {withdrawals.map(w => (
                          <tr key={w.id} className="border-t border-gray-50">
                            <td className="p-3 font-medium">{w.user_name}</td>
                            <td className="p-3 text-center font-semibold">Rs.{w.amount}</td>
                            <td className="p-3 text-center text-xs">{w.upi_id || '-'}</td>
                            <td className="p-3 text-center">{statusBadge(w.status)}</td>
                            <td className="p-3 text-center text-xs">{new Date(w.created_at).toLocaleDateString()}</td>
                            <td className="p-3 text-center">{w.status === 'pending' && <div className="flex gap-1 justify-center"><Button size="sm" onClick={() => handleAction(processWithdrawal, w.id, 'completed')} className="bg-green-600 text-white rounded-lg text-xs h-8">Approve</Button><Button size="sm" variant="outline" onClick={() => handleAction(processWithdrawal, w.id, 'rejected')} className="border-red-300 text-red-600 rounded-lg text-xs h-8">Reject</Button></div>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {withdrawals.length === 0 && <div className="text-center py-12 text-gray-400 text-sm">No withdrawals</div>}
                  </div>
                </div>
              )}

              {/* ===== TRANSACTIONS ===== */}
              {activeTab === 'transactions' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-sm">All Transactions ({transactionsPagination.total.toLocaleString()})</h3></div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide"><tr><th className="p-3 text-left font-medium">Invoice</th><th className="p-3 text-left font-medium">Type</th><th className="p-3 text-left font-medium">User</th><th className="p-3 font-medium">Amount</th><th className="p-3 font-medium">Status</th><th className="p-3 font-medium">Date</th></tr></thead>
                      <tbody>
                        {transactions.map(t => (
                          <tr key={t.id} className="border-t border-gray-50">
                            <td className="p-3 font-mono text-xs">{t.invoice_number || '-'}</td>
                            <td className="p-3">{badge(t.type, t.type === 'membership' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')}</td>
                            <td className="p-3">{t.user_name || '-'}</td>
                            <td className="p-3 text-center font-semibold">Rs.{t.amount}</td>
                            <td className="p-3 text-center">{badge('completed', 'bg-emerald-100 text-emerald-700')}</td>
                            <td className="p-3 text-center text-xs">{new Date(t.created_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {transactions.length === 0 && <div className="text-center py-12 text-gray-400 text-sm">No transactions</div>}
                  </div>
                  <Pagination page={transactionsPagination.page} pages={transactionsPagination.pages} total={transactionsPagination.total} label="transactions" onPageChange={fetchTransactions} />
                </div>
              )}

              {/* ===== REPORTS ===== */}
              {activeTab === 'reports' && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="font-semibold text-sm text-gray-900 mb-4 flex items-center gap-2"><Download className="w-4 h-4" /> Download Reports</h3>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <div><label className="text-xs text-gray-500 block mb-1">Start Date</label><Input type="date" value={reportDates.start} onChange={e => setReportDates(p => ({...p, start: e.target.value}))} className="w-44 h-9" /></div>
                    <div><label className="text-xs text-gray-500 block mb-1">End Date</label><Input type="date" value={reportDates.end} onChange={e => setReportDates(p => ({...p, end: e.target.value}))} className="w-44 h-9" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { type: 'users', label: 'Users Report', desc: 'All registered users', icon: Users, accent: 'border-blue-200 bg-blue-50' },
                      { type: 'registrations', label: 'Registrations', desc: 'Registration data', icon: UserCheck, accent: 'border-green-200 bg-green-50' },
                      { type: 'bookings', label: 'Bookings Report', desc: 'All bookings data', icon: Calendar, accent: 'border-purple-200 bg-purple-50' },
                      { type: 'transactions', label: 'Transactions', desc: 'All payment data', icon: DollarSign, accent: 'border-orange-200 bg-orange-50' }
                    ].map(r => (
                      <div key={r.type} className={`border rounded-xl p-5 ${r.accent}`}>
                        <r.icon className="w-7 h-7 mb-3 opacity-70" />
                        <h4 className="font-semibold text-sm mb-1">{r.label}</h4>
                        <p className="text-xs opacity-60 mb-4">{r.desc}</p>
                        <Button size="sm" onClick={() => handleDownloadReport(r.type)} className="bg-gray-900 text-white rounded-lg text-xs w-full h-8"><Download className="w-3 h-3 mr-1" /> Download CSV</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Screenshot Viewer */}
      {viewScreenshot.open && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setViewScreenshot({ open: false, data: null, paymentId: null })}>
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">Screenshot — {viewScreenshot.paymentId}</h3>
              <button onClick={() => setViewScreenshot({ open: false, data: null, paymentId: null })} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            {viewScreenshot.data ? <img src={`data:image/jpeg;base64,${viewScreenshot.data}`} alt="Payment" className="w-full rounded-lg" /> : <p className="text-gray-500 text-center py-8">Loading...</p>}
          </div>
        </div>
      )}

      {/* Tax-Invoices-per-User Modal (extracted to ../components/AdminInvoicesModal) */}
      <AdminInvoicesModal
        open={invoicesModal.open}
        user={invoicesModal.user}
        invoices={invoicesModal.invoices}
        loading={invoicesModal.loading}
        preview={invoicePreview}
        onClose={closeInvoicesModal}
        onPreview={handlePreviewInvoice}
        onClosePreview={() => {
          try { URL.revokeObjectURL(invoicePreview.url); } catch (_) { /* already revoked */ }
          setInvoicePreview({ url: null, invoiceNo: null });
        }}
        onDownload={handleDownloadInvoice}
      />
    </div>
  );
};

export default Admin;
